import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Send, Loader2, Bot, User, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ORIGIN = process.env.REACT_APP_BACKEND_URL;

// Normalise URLs the LLM may emit:
//   - strip "cover_image=" / "image:" prefix if any
//   - resolve site-relative paths against the backend origin (only for images,
//     not for in-app routes like /courses/foo)
function resolveImgSrc(raw) {
  let s = (raw || "").trim().replace(/^(cover_image\s*=|image\s*:\s*)/i, "").trim();
  if (s.startsWith("/")) s = `${ORIGIN}${s}`;
  return s;
}

// Render an assistant message that may contain Markdown image and link
// syntax: `![alt](url)`, `[label](url)`, and the linked-image form
// `[![alt](img)](href)`. Course covers become clickable cards that
// navigate in-app to /courses/{slug}.
function MessageContent({ content, onNavigate }) {
  // Tokenise. Order matters: linked-image first, then image, then link.
  const re = /(\[!\[[^\]]*\]\([^)]+\)\]\([^)]+\)|!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\))/g;
  const parts = content.split(re);
  return (
    <div className="text-sm whitespace-pre-wrap leading-relaxed">
      {parts.map((part, i) => {
        // Linked image: [![alt](img)](href)
        let m = part.match(/^\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)$/);
        if (m) {
          const alt = m[1] || "Course cover";
          const src = resolveImgSrc(m[2]);
          const href = m[3].trim();
          return (
            <button
              key={i}
              type="button"
              onClick={() => onNavigate(href)}
              className="group block w-full max-w-[260px] my-2 rounded-lg border border-zinc-700 hover:border-[#00F0FF] overflow-hidden text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]/60"
            >
              <img
                src={src}
                alt={alt}
                loading="lazy"
                className="w-full block"
                style={{ aspectRatio: "16 / 9", objectFit: "cover" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <span className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-zinc-900 text-[11px] font-semibold text-white">
                <span className="truncate">{alt}</span>
                <span className="inline-flex items-center gap-1 text-[#00F0FF] group-hover:translate-x-0.5 transition-transform">
                  View <ArrowRight size={11} />
                </span>
              </span>
            </button>
          );
        }
        // Plain image: ![alt](url)
        m = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (m) {
          const alt = m[1] || "Course cover";
          const src = resolveImgSrc(m[2]);
          return (
            <img
              key={i}
              src={src}
              alt={alt}
              loading="lazy"
              className="block my-2 w-full max-w-[260px] rounded-lg border border-zinc-700"
              style={{ aspectRatio: "16 / 9", objectFit: "cover" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          );
        }
        // Plain link: [label](url)
        m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (m) {
          const label = m[1];
          const href = m[2].trim();
          const isInternal = href.startsWith("/");
          return (
            <button
              key={i}
              type="button"
              onClick={() => onNavigate(href)}
              className="text-[#00F0FF] underline underline-offset-2 hover:text-white inline"
            >
              {label}
              {isInternal ? null : " ↗"}
            </button>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </div>
  );
}

/* SVG Robot Mascot */
function RobotMascot({ onClick }) {
  return (
    <div
      onClick={onClick}
      className="cursor-pointer select-none"
      style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.4))" }}
    >
      <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Body */}
        <rect x="25" y="45" width="50" height="45" rx="12" fill="#1a1a2e" stroke="#00F0FF" strokeWidth="1.5"/>
        {/* Body glow stripe */}
        <rect x="38" y="65" width="24" height="3" rx="1.5" fill="#7000FF" opacity="0.8"/>
        <rect x="42" y="72" width="16" height="2" rx="1" fill="#00F0FF" opacity="0.5"/>

        {/* Head */}
        <rect x="22" y="12" width="56" height="38" rx="14" fill="#111127" stroke="#00F0FF" strokeWidth="2"/>
        {/* Visor/screen */}
        <rect x="30" y="20" width="40" height="20" rx="8" fill="#0a0a1a"/>
        {/* Eyes */}
        <rect x="36" y="28" width="10" height="3.5" rx="1.75" fill="#00F0FF">
          <animate attributeName="opacity" values="1;1;0.3;1;1" dur="3s" repeatCount="indefinite"/>
        </rect>
        <rect x="54" y="28" width="10" height="3.5" rx="1.75" fill="#00F0FF">
          <animate attributeName="opacity" values="1;1;0.3;1;1" dur="3s" repeatCount="indefinite"/>
        </rect>

        {/* Antenna */}
        <line x1="50" y1="12" x2="50" y2="4" stroke="#7000FF" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="50" cy="3" r="3" fill="#00F0FF">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite"/>
        </circle>

        {/* Left Arm raised */}
        <path d="M25 55 L12 30 L8 28" stroke="#1a1a2e" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M25 55 L12 30 L8 28" stroke="#00F0FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="8" cy="26" r="4" fill="#1a1a2e" stroke="#00F0FF" strokeWidth="1"/>

        {/* Right Arm raised */}
        <path d="M75 55 L88 30 L92 28" stroke="#1a1a2e" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M75 55 L88 30 L92 28" stroke="#7000FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="92" cy="26" r="4" fill="#1a1a2e" stroke="#7000FF" strokeWidth="1"/>

        {/* Legs */}
        <rect x="34" y="88" width="12" height="18" rx="5" fill="#1a1a2e" stroke="#00F0FF" strokeWidth="1"/>
        <rect x="54" y="88" width="12" height="18" rx="5" fill="#1a1a2e" stroke="#7000FF" strokeWidth="1"/>
        {/* Feet */}
        <ellipse cx="40" cy="108" rx="9" ry="5" fill="#1a1a2e" stroke="#00F0FF" strokeWidth="1"/>
        <ellipse cx="60" cy="108" rx="9" ry="5" fill="#1a1a2e" stroke="#7000FF" strokeWidth="1"/>
      </svg>
    </div>
  );
}

export default function ChatWidget() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm your ShansoAI assistant. I can help you with:\n\n\u2022 ShansoLearn — flagship courses & 20-min Quick Bites\n\u2022 Live AI workshops & cohort programs\n\u2022 Digital documents & templates\n\u2022 Custom website development\n\nWhat would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  function handleNavigate(href) {
    if (!href) return;
    if (href.startsWith("/")) {
      setIsOpen(false);
      navigate(href);
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      }, {
        timeout: 60000
      });

      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      console.error("Chat error:", err);
      let errorMessage = "I'm sorry, I'm having trouble connecting right now. Please try again or contact us directly at support@shansoai.com";
      if (err.code === 'ECONNABORTED') {
        errorMessage = "The response is taking too long. Please try asking a simpler question or contact us directly.";
      }
      setMessages(prev => [...prev, { role: "assistant", content: errorMessage }]);
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    "What flagship courses do you offer?",
    "Tell me about the Quick Bites",
    "Do I get a certificate?",
    "What's the refund policy?"
  ];

  return (
    <>
      {/* Robot Mascot + Banner (when chat is closed) */}
      {!isOpen && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center" data-testid="chat-mascot-container">
          {/* Banner Card */}
          {!bannerDismissed && (
            <div
              className="relative mb-[-8px] cursor-pointer animate-[fadeSlideUp_0.5s_ease-out]"
              onClick={() => setIsOpen(true)}
              data-testid="chat-banner"
            >
              <div className="relative bg-gradient-to-br from-[#e8f7ff] to-[#f0e8ff] rounded-xl px-5 py-3 shadow-xl border border-white/60"
                style={{ minWidth: 200 }}
              >
                {/* Close button */}
                <button
                  onClick={(e) => { e.stopPropagation(); setBannerDismissed(true); }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors border border-gray-200"
                  data-testid="chat-banner-close"
                >
                  <X size={12} className="text-gray-500" />
                </button>

                {/* 24x7 badge */}
                <div className="inline-block bg-[#00F0FF] text-[#0a0a1a] text-[10px] font-bold px-2.5 py-0.5 rounded-full mb-1.5 tracking-wide">
                  24x7
                </div>

                {/* Banner text */}
                <div>
                  <p className="text-[#1a1a4e] font-bold text-base leading-tight tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    ShansoAI
                  </p>
                  <p className="text-[#7000FF] font-semibold text-sm leading-tight">
                    Assistant for you
                  </p>
                </div>
              </div>

              {/* Small triangle pointer */}
              <div className="flex justify-center">
                <div className="w-3 h-3 bg-gradient-to-br from-[#e8f7ff] to-[#f0e8ff] rotate-45 -mt-1.5 border-r border-b border-white/60" />
              </div>
            </div>
          )}

          {/* Robot Character */}
          <div
            className="animate-[bobble_3s_ease-in-out_infinite] hover:scale-105 transition-transform"
            data-testid="chat-robot-trigger"
          >
            <RobotMascot onClick={() => setIsOpen(true)} />
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 w-[380px] max-w-[calc(100vw-48px)] h-[520px] max-h-[calc(100vh-100px)] bg-[#111] border border-zinc-800 shadow-2xl z-50 flex flex-col rounded-xl overflow-hidden animate-[scaleIn_0.2s_ease-out]"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00F0FF] to-[#7000FF] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>ShansoAI Assistant</h3>
                <p className="text-white/70 text-xs">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              data-testid="chat-close-btn"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === "user"
                    ? "bg-[#00F0FF]"
                    : "bg-gradient-to-br from-[#00F0FF] to-[#7000FF]"
                }`}>
                  {msg.role === "user" ? (
                    <User size={16} className="text-black" />
                  ) : (
                    <Bot size={16} className="text-white" />
                  )}
                </div>
                <div className={`max-w-[75%] px-4 py-2 rounded-lg ${
                  msg.role === "user"
                    ? "bg-[#00F0FF] text-black"
                    : "bg-zinc-800 text-white"
                }`}>
                  {msg.role === "assistant"
                    ? <MessageContent content={msg.content} onNavigate={handleNavigate} />
                    : <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center flex-shrink-0">
                  <Bot size={16} className="text-white" />
                </div>
                <div className="bg-zinc-800 px-4 py-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 size={18} className="animate-spin text-[#00F0FF]" />
                    <span className="text-zinc-400 text-xs">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && !loading && (
            <div className="px-4 pb-2">
              <p className="text-zinc-500 text-xs mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(q)}
                    className="text-xs px-3 py-1.5 bg-zinc-800 text-[#00F0FF] border border-zinc-700 hover:border-[#00F0FF] transition-colors rounded-full"
                    data-testid={`quick-question-${idx}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-zinc-800">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#00F0FF] focus:outline-none rounded-full"
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] rounded-full flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="chat-send-btn"
              >
                <Send size={18} className="text-white" />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes bobble {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}
